import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import EmptyState from "../components/EmptyState/EmptyState";
import FooterHourlyTracking from "../components/footers/FooterHourlyTracking";
import NavBarHourlyTracking from "../components/navbars/NavBarHourlyTracking";
import PageContainer from "../components/pageContainer/PageContainer";
import TableHourTrackingMission from "../components/tables/TableHourTrackingMission";
import useTitle from "../hooks/TitleManager";
import UseContext from "../hooks/useContext";
import ApiDatabase from "../server";
import { forceDateToUtc0, dateToYYYYMMDD } from "../utils/Date";
const { dateLong } = require("../utils/Date");

const HourlyTracking = () => {
  useTitle({ title: "Suivi horaire" });
  const {
    slideOverDataReturn,
    setSlideOverDataReturn,
    missions,
    setMissions,
    companySelected,
    setCompanySelected,
    companyList,
    setCompanyList,
    dateStart,
    setDateStart,
    dateEnd,
    setDateEnd,
  } = UseContext();
  const navigate = useNavigate();
  const [missionsToDisplay, setMissionsToDisplay] = useState([]);
  const [interimAgencyList, setInterimAgencyList] = useState([]);
  const [interimAgencySelected, setInterimAgencySelected] = useState([]);
  const [interimSectorList, setInterimSectorList] = useState([]);
  const [interimSectorSelected, setInterimSectorSelected] = useState([]);
  const [isWaitingMissions, setIsWaitingMissions] = useState(true);
  const [refreshMissions, setRefreshMissions] = useState(false);
  const [search, setSearch] = useState("");
  const [missionStatusSelected, setMissionStatusSelected] = useState([]);
  const [missionStatusList, setMissionStatusList] = useState([
    { type: "inProgress", name: "En cours", checked: false },
    { type: "flexibility", name: "Souplesse", checked: false },
    { type: "unsigned", name: "Non émargé", checked: false },
    { type: "shiftCompleted", name: "Shift terminé", checked: false },
    { type: "shiftMissing", name: "Shift manquant", checked: false },
    { type: "missingPhoneNumber", name: "Numéro manquant", checked: false },
  ]);

  const [currentPage, setCurrentPage] = useState(1);
  const countPerPage = 25;
  const [countTotal, setCountTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [initialParamsLoaded, setInitialParamsLoaded] = useState(false);
  const [isInterimAgencyListLoaded, setIsInterimAgencyListLoaded] =
    useState(false);
  const [isInterimSectorListLoaded, setIsInterimSectorListLoaded] =
    useState(false);
  const [isCompanyListLoaded, setIsCompanyListLoaded] = useState(false);

  let timerGetMissionSuggestions = useRef(null);
  const isLoadingRef = useRef(false);

  // Filter and group missions by date within [dateStart, dateEnd]
  useEffect(() => {
    if (missions.length > 0 && dateStart && dateEnd) {
      const startStr = dateStart.toISOString().split("T")[0];
      const endStr = dateEnd.toISOString().split("T")[0];
      const dateNow = new Date().setHours(0, 0, 0, 0);

      let filteredByStatus;
      if (missionStatusSelected.length > 0) {
        filteredByStatus = missions.filter((mission) => {
          const dateMission = forceDateToUtc0(mission.date).setHours(
            0,
            0,
            0,
            0
          );
          const isDateBeforeDateNow = dateMission < dateNow;
          const isDateAfterDateNow = dateMission > dateNow;

          return missionStatusSelected.some((status) => {
            switch (status.type) {
              case "flexibility":
                return mission.isFlexibilityDate;
              case "inProgress":
                return (
                  (mission.markup.start &&
                    mission.markup.end &&
                    !isDateBeforeDateNow &&
                    !isDateAfterDateNow) ||
                  (mission.markup.start &&
                    !mission.markup.end &&
                    !isDateBeforeDateNow &&
                    !isDateAfterDateNow)
                );
              case "unsigned":
                return (
                  !mission.markup.start &&
                  !mission.markup.end &&
                  !isDateAfterDateNow
                );
              case "shiftCompleted":
                return (
                  mission.markup.start &&
                  mission.markup.end &&
                  !isDateAfterDateNow &&
                  isDateBeforeDateNow
                );
              case "shiftMissing":
                return (
                  mission.markup.start &&
                  !mission.markup.end &&
                  isDateBeforeDateNow &&
                  !isDateAfterDateNow
                );
              case "missingPhoneNumber":
                return !mission.user.phone;
              default:
                return false;
            }
          });
        });
      } else {
        filteredByStatus = missions;
      }

      const filteredByDate = filteredByStatus.filter((mission) => {
        const dateMission = new Date(mission.date).toISOString().split("T")[0];
        return dateMission >= startStr && dateMission <= endStr;
      });

      const missionsByUserAndDate = {};

      filteredByDate.forEach((mission) => {
        const key = `${mission.user.idTempWorker}-${new Date(mission.date).toISOString().split("T")[0]}`;
        if (!missionsByUserAndDate[key]) {
          missionsByUserAndDate[key] = [];
        }
        missionsByUserAndDate[key].push(mission);
      });

      const finalMissions = [];

      // Sorting of assignments: non-flexible first, then flexible
      Object.keys(missionsByUserAndDate).forEach((key) => {
        const missions = missionsByUserAndDate[key];

        const nonFlexibleMissions = missions.filter(
          (mission) => mission.isFlexibilityDate === false
        );
        let flexibleMissions = missions.filter(
          (mission) => mission.isFlexibilityDate === true
        );

        // If we have both a non-flexible mission and a flexible mission, then we filter out the flexible missions whose "timesheet" is empty
        if (nonFlexibleMissions.length > 0 && flexibleMissions.length > 0) {
          flexibleMissions = flexibleMissions.filter(
            (mission) =>
              Array.isArray(mission.timeSheets) && mission.timeSheets.length > 0
          );
        }

        finalMissions.push(...nonFlexibleMissions);
        finalMissions.push(...flexibleMissions);
      });

      // Sort by date ascending for grouped display
      finalMissions.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      setCountTotal(finalMissions.length);
      setTotalPages(Math.ceil(finalMissions.length / countPerPage));
      const start = (currentPage - 1) * countPerPage;
      const end = start + countPerPage;

      setMissionsToDisplay(finalMissions.slice(start, end));
    } else {
      setMissionsToDisplay([]);
    }
  }, [
    missions,
    dateStart,
    dateEnd,
    currentPage,
    countPerPage,
    missionStatusSelected,
    setMissionsToDisplay,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    dateStart,
    dateEnd,
    search,
    companySelected,
    interimAgencySelected,
    interimSectorSelected,
    missionStatusList,
  ]);

  useEffect(() => {
    if (
      isInterimAgencyListLoaded &&
      isInterimSectorListLoaded &&
      isCompanyListLoaded &&
      missionStatusList.length > 0 &&
      !initialParamsLoaded
    ) {
      const params = new URLSearchParams(window.location.search);

      const dateStartParam = params.get("dateStart");
      if (dateStartParam && new Date(dateStartParam).toISOString() !== dateStart?.toISOString()) {
        setDateStart(new Date(dateStartParam));
      }

      const dateEndParam = params.get("dateEnd");
      if (dateEndParam && new Date(dateEndParam).toISOString() !== dateEnd?.toISOString()) {
        setDateEnd(new Date(dateEndParam));
      }

      const searchParam = params.get("search");
      if (searchParam && searchParam !== search) {
        setSearch(searchParam);
      }

      const idCompany = params.get("companySelected");
      if (idCompany) {
        const selectedCompany = companyList.find(
          (company) => company.idCompany === idCompany
        );
        if (
          selectedCompany &&
          selectedCompany.idCompany !== companySelected?.idCompany
        ) {
          setCompanySelected(selectedCompany);
        }
      }

      const interimAgencies = params.get("interimAgencySelected");
      if (interimAgencies) {
        const selectedAgencies = interimAgencies
          .split(",")
          .map((name) =>
            interimAgencyList.find((agency) => agency.name === name)
          )
          .filter((agency) => agency);
        const updatedAgencyList = interimAgencyList.map((interimAgency) => ({
          ...interimAgency,
          checked: interimAgencies.split(",").includes(interimAgency.name),
        }));

        if (
          JSON.stringify(updatedAgencyList) !==
            JSON.stringify(interimAgencyList) &&
          JSON.stringify(selectedAgencies) !==
            JSON.stringify(interimAgencySelected)
        ) {
          setInterimAgencyList(updatedAgencyList);
          setInterimAgencySelected(selectedAgencies);
        }
      }

      const interimSectors = params.get("interimSectorSelected");
      if (interimSectors) {
        const selectedSectors = interimSectors
          .split(",")
          .map((name) =>
            interimSectorList.find((sector) => sector.name === name)
          )
          .filter((sector) => sector);
        const updatedInterimSectorList = interimSectorList.map(
          (interimSector) => ({
            ...interimSector,
            checked: interimSectors.split(",").includes(interimSector.name),
          })
        );

        if (
          JSON.stringify(updatedInterimSectorList) !==
            JSON.stringify(interimSectorList) &&
          JSON.stringify(selectedSectors) !==
            JSON.stringify(interimSectorSelected)
        ) {
          setInterimSectorList(updatedInterimSectorList);
          setInterimSectorSelected(selectedSectors);
        }
      }

      const missionStatuses = params.get("missionStatusList");
      if (missionStatuses) {
        const selectedMissionStatus = missionStatuses
          .split(",")
          .map((name) =>
            missionStatusList.find(
              (missionStatus) => missionStatus.name === name
            )
          )
          .filter((missionStatus) => missionStatus);
        const updatedMissionStatusList = missionStatusList.map(
          (missionStatus) => ({
            ...missionStatus,
            checked: missionStatuses.split(",").includes(missionStatus.name),
          })
        );

        if (
          JSON.stringify(updatedMissionStatusList) !==
            JSON.stringify(missionStatusList) &&
          JSON.stringify(selectedMissionStatus) !==
            JSON.stringify(missionStatusSelected)
        ) {
          setMissionStatusList(updatedMissionStatusList);
          setMissionStatusSelected(selectedMissionStatus);
        }
      }

      setInitialParamsLoaded(true);
    }
  }, [
    isInterimAgencyListLoaded,
    isInterimSectorListLoaded,
    isCompanyListLoaded,
    initialParamsLoaded,
    interimAgencyList,
    interimSectorList,
    missionStatusList,
    companyList,
    dateStart,
    dateEnd,
    search,
    companySelected,
    interimAgencySelected,
    interimSectorSelected,
    missionStatusSelected,
    setCompanySelected,
    setDateStart,
    setDateEnd,
  ]);

  useEffect(() => {
    ApiDatabase.getInterimAgencyMissions(
      {},
      (data) => {
        if (data.interimAgencies?.length > 0) {
          const selectedAgencyNames = interimAgencySelected?.map(
            (agency) => agency.name
          );

          data.interimAgencies.forEach((agency) => {
            agency.checked = selectedAgencyNames.includes(agency.name);
          });

          setInterimAgencyList(data.interimAgencies);
        } else {
          setInterimAgencyList([]);
        }
        setIsInterimAgencyListLoaded(true);
      },
      (err) => {
        console.log(err);
        setIsInterimAgencyListLoaded(true);
      }
    );
  }, [
    refreshMissions,
    setInterimAgencySelected,
    setInterimAgencyList,
    interimAgencySelected,
  ]);

  useEffect(() => {
    ApiDatabase.getInterimSectorMissions(
      {},
      (data) => {
        if (data.interimSectors?.length > 0) {
          const selectedSectorNames = interimSectorSelected?.map(
            (sector) => sector.name
          );

          data.interimSectors.forEach((sector) => {
            sector.checked = selectedSectorNames.includes(sector.name);
          });

          setInterimSectorList(data.interimSectors);
        } else {
          setInterimSectorList([]);
        }
        setIsInterimSectorListLoaded(true);
      },
      (err) => {
        console.log(err);
        setIsInterimSectorListLoaded(true);
      }
    );
  }, [
    refreshMissions,
    setInterimSectorSelected,
    setInterimSectorList,
    interimSectorSelected,
  ]);

  useEffect(() => {
    ApiDatabase.getCompaniesMissions(
      {},
      (data) => {
        if (data.companies?.length > 0) {
          let selectedCompanyIndex = -1;

          if (companySelected) {
            selectedCompanyIndex = data.companies.findIndex(
              (company) => company._id === companySelected.idCompany
            );
          }

          data.companies.forEach((company, index) => {
            company.checked =
              index ===
              (selectedCompanyIndex !== -1 ? selectedCompanyIndex : 0);
          });

          if (selectedCompanyIndex === -1) {
            const firstCompany = data.companies[0];
            setCompanySelected({
              idCompany: firstCompany._id,
              name: firstCompany.companyName,
              syncDate: firstCompany.lastSyncDate,
              badgerName: firstCompany.badgerName,
            });
          }

          setCompanyList(
            data.companies.map((company) => ({
              idCompany: company._id,
              name: company.companyName || company.name,
              syncDate: company.lastSyncDate,
              badgerName: company.badgerName,
              checked: company.checked,
            }))
          );
        } else {
          setCompanyList([]);
        }
        setIsCompanyListLoaded(true);
      },
      (err) => {
        console.log(err);
        setIsCompanyListLoaded(true);
      }
    );
  }, [setCompanySelected, setCompanyList, companySelected]);

  // Sync URL params with current state
  useEffect(() => {
    if (initialParamsLoaded) {
      const params = new URLSearchParams(window.location.search);

      if (dateStart) {
        params.set("dateStart", dateStart.toISOString());
      } else {
        params.delete("dateStart");
      }

      if (dateEnd) {
        params.set("dateEnd", dateEnd.toISOString());
      } else {
        params.delete("dateEnd");
      }

      if (search) {
        params.set("search", search);
      } else {
        params.delete("search");
      }

      if (companySelected?.idCompany) {
        params.set("companySelected", companySelected.idCompany);
      } else {
        params.delete("companySelected");
      }

      if (interimAgencySelected?.length > 0) {
        const agencies = interimAgencySelected
          .map((interim) => interim.name)
          .join(",");
        params.set("interimAgencySelected", agencies);
      } else {
        params.delete("interimAgencySelected");
      }

      if (interimSectorSelected?.length > 0) {
        const sectors = interimSectorSelected
          .map((interim) => interim.name)
          .join(",");
        params.set("interimSectorSelected", sectors);
      } else {
        params.delete("interimSectorSelected");
      }

      const selectedMissions = missionStatusList
        ?.filter((missionStatus) => missionStatus.checked)
        .map((missionStatus) => missionStatus.name);

      if (selectedMissions?.length > 0) {
        params.set("missionStatusList", selectedMissions.join(","));
      } else {
        params.delete("missionStatusList");
      }

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState(null, "", newUrl);
    }
  }, [
    dateStart,
    dateEnd,
    search,
    companySelected,
    interimAgencySelected,
    interimSectorSelected,
    missionStatusList,
    initialParamsLoaded,
  ]);

  const manageGetMission = useCallback(async (silent = false) => {
    if (!initialParamsLoaded) return;
    if (!dateStart || !dateEnd) return;

    if (!silent) setIsWaitingMissions(true);
    isLoadingRef.current = true;

    try {
      const idCompany = companySelected?.idCompany || "";

      if (idCompany) {
        const getMissionsPromise = () =>
          new Promise((resolve, reject) => {
            ApiDatabase.getMissions(
              {
                idCompany,
                interimAgencies:
                  interimAgencySelected.length > 0
                    ? JSON.stringify(
                        interimAgencySelected.map((agency) => agency.name)
                      )
                    : [],
                interimSectors:
                  interimSectorSelected.length > 0
                    ? JSON.stringify(
                        interimSectorSelected.map((sector) => sector.name)
                      )
                    : [],
                search,
                dateFrom: dateToYYYYMMDD(dateStart),
                dateTo: dateToYYYYMMDD(dateEnd),
              },
              (data) => resolve(data),
              (err) => reject(err)
            );
          });

        const missionData = await getMissionsPromise();

        if (idCompany === missionData.companyId) {
          setMissions(missionData.missions || []);
        }
        isLoadingRef.current = false;
        if (!silent) setIsWaitingMissions(false);
      } else {
        setMissions([]);
        isLoadingRef.current = false;
        if (!silent) setIsWaitingMissions(false);
      }
    } catch (err) {
      if (err.name !== "CanceledError") {
        console.log(err);
        isLoadingRef.current = false;
        if (!silent) setIsWaitingMissions(false);
      }
    }
  }, [
    initialParamsLoaded,
    companySelected?.idCompany,
    interimAgencySelected,
    interimSectorSelected,
    search,
    dateStart,
    dateEnd,
    setIsWaitingMissions,
    setMissions,
  ]);

  // Reload on filter changes (company, agencies, sectors)
  useEffect(() => {
    if (initialParamsLoaded) {
      setMissions([]);
      manageGetMission();
    }
    // eslint-disable-next-line
  }, [
    initialParamsLoaded,
    companySelected,
    interimAgencySelected,
    interimSectorSelected,
  ]);

  // Reload on search change (debounced)
  useEffect(() => {
    if (initialParamsLoaded) {
      setIsWaitingMissions(true);
      setMissions([]);
      timerGetMissionSuggestions.current = setTimeout(async () => {
        await manageGetMission();
      }, 300);

      return () => clearTimeout(timerGetMissionSuggestions.current);
    }
    // eslint-disable-next-line
  }, [initialParamsLoaded, search]);

  // Reload on period change (debounced)
  useEffect(() => {
    if (initialParamsLoaded && dateStart && dateEnd) {
      setIsWaitingMissions(true);
      setMissions([]);
      timerGetMissionSuggestions.current = setTimeout(async () => {
        await manageGetMission();
      }, 300);

      return () => clearTimeout(timerGetMissionSuggestions.current);
    }
    // eslint-disable-next-line
  }, [initialParamsLoaded, dateStart, dateEnd]);

  useEffect(() => {
    if (slideOverDataReturn.missions) {
      setRefreshMissions(!refreshMissions);
      setSlideOverDataReturn({});
    }
  }, [slideOverDataReturn, setSlideOverDataReturn, refreshMissions]);

  useEffect(() => {
    ApiDatabase.getInterimAgencyMissions(
      {},
      (data) => {
        if (data.interimAgencies?.length > 0) {
          const selectedAgencyNames = interimAgencySelected?.map(
            (agency) => agency.name
          );

          data.interimAgencies.forEach((agency) => {
            agency.checked = selectedAgencyNames.includes(agency.name);
          });

          setInterimAgencyList(data.interimAgencies);
        } else {
          setInterimAgencyList([]);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  }, [
    refreshMissions,
    setInterimAgencySelected,
    setInterimAgencyList,
    interimAgencySelected,
  ]);

  useEffect(() => {
    ApiDatabase.getInterimSectorMissions(
      {},
      (data) => {
        if (data.interimSectors?.length > 0) {
          const selectedSectorNames = interimSectorSelected?.map(
            (sector) => sector.name
          );

          data.interimSectors.forEach((sector) => {
            sector.checked = selectedSectorNames.includes(sector.name);
          });

          setInterimSectorList(data.interimSectors);
        } else {
          setInterimSectorList([]);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  }, [
    refreshMissions,
    setInterimSectorSelected,
    setInterimSectorList,
    interimSectorSelected,
  ]);

  const navigateToMission = (idMission, date) => {
    const params = new URLSearchParams(window.location.search);
    params.set("date", date);
    const newUrl = `/hourly-tracking/${idMission}?${params.toString()}`;
    navigate(newUrl);
  };

  // Group paginated missions by date for display
  const missionsGroupedByDate = {};
  missionsToDisplay.forEach((mission) => {
    const dateKey = new Date(mission.date).toISOString().split("T")[0];
    if (!missionsGroupedByDate[dateKey]) {
      missionsGroupedByDate[dateKey] = [];
    }
    missionsGroupedByDate[dateKey].push(mission);
  });

  const isSingleDay =
    dateStart &&
    dateEnd &&
    dateStart.toISOString().split("T")[0] ===
      dateEnd.toISOString().split("T")[0];

  return (
    <PageContainer
      type={"hourly_tracking"}
      dateStart={dateStart}
      setDateStart={setDateStart}
      dateEnd={dateEnd}
      setDateEnd={setDateEnd}
    >
      <div className={"flex flex-1 flex-col gap-4 h-full"}>
        <NavBarHourlyTracking
          dateStart={dateStart}
          setDateStart={setDateStart}
          dateEnd={dateEnd}
          setDateEnd={setDateEnd}
          companySelected={companySelected}
          companyList={companyList}
          setCompanySelected={setCompanySelected}
          setCompanyList={setCompanyList}
          interimAgencyList={interimAgencyList}
          setInterimAgencyList={setInterimAgencyList}
          interimAgencySelected={interimAgencySelected}
          setInterimAgencySelected={setInterimAgencySelected}
          interimSectorList={interimSectorList}
          setInterimSectorList={setInterimSectorList}
          interimSectorSelected={interimSectorSelected}
          setInterimSectorSelected={setInterimSectorSelected}
          search={search}
          setSearch={setSearch}
          missionStatusSelected={missionStatusSelected}
          setMissionStatusSelected={setMissionStatusSelected}
          missionStatusList={missionStatusList}
          setMissionStatusList={setMissionStatusList}
        />
        <div className={"h-0.5 w-full bg-gray-100"} />
        <div className={"flex flex-1 flex-col h-full overflow-auto"}>
          {isWaitingMissions ? (
            <div className={"flex flex-col gap-3 min-w-[830px] animate-pulse"}>
              {[1, 2, 3, 4, 5].map((index) => (
                <TableHourTrackingMission key={index} isWaiting={true} />
              ))}
            </div>
          ) : (
            <>
              {missionsToDisplay.length > 0 ? (
                isSingleDay ? (
                  <div className={"flex flex-col gap-3 min-w-[830px]"}>
                    {missionsToDisplay.map((mission, index) => (
                      <TableHourTrackingMission
                        key={index}
                        mission={mission}
                        navigateToMission={navigateToMission}
                      />
                    ))}
                  </div>
                ) : (
                  <div className={"flex flex-col gap-4 min-w-[830px]"}>
                    {Object.entries(missionsGroupedByDate).map(([dateKey, dateMissions]) => (
                      <div key={dateKey}>
                        <div className="flex gap-1 items-center p-3">
                          <div className="text-2xl text-workfully-700">·</div>
                          <div className={"font-semibold capitalize px-1 py-2"}>
                            {dateLong(new Date(dateKey))}
                          </div>
                        </div>
                        <div className={"flex flex-col gap-3"}>
                          {dateMissions.map((mission, index) => (
                            <TableHourTrackingMission
                              key={index}
                              mission={mission}
                              navigateToMission={navigateToMission}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <EmptyState type={"hourly_tracking"} />
              )}
            </>
          )}
        </div>
        <FooterHourlyTracking
          syncDate={companySelected?.syncDate || null}
          badgerName={companySelected?.badgerName || null}
          currentPage={currentPage}
          countPerPage={countPerPage}
          countTotal={countTotal}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </PageContainer>
  );
};

export default HourlyTracking;
